import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { usePrevious } from 'react-use'
import { bindActionCreators } from 'redux'

import { getCurrentSpaceId } from '@/components/App/selectors'
import Page from '@/components/Page'
import { startNotification } from '@/components/Publisher/actions'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { resetRequests } from '@/store/actions'
import { isRequestError, isRequestPending } from '@/store/selectors'

import {
  deleteMultilingualNotification,
  fetchMultilingualNotificationsPaginated,
  fetchNotificationSources,
  recallMultilingualNotification
} from './actions'
import { DELETE_NOTIFICATION, FETCH_NOTIFICATIONS_PAGINATED, RECALL_NOTIFICATION } from './actionTypes'
import LandingStep from './LandingStep'
import messages from './messages'
import PublisherNotificationList from './PublisherNotificationList'
import { getNotificationsPage, getPageStartIndex, getTotalNotificationCount } from './selectors'

const errorsToHandle = [
  {
    type: FETCH_NOTIFICATIONS_PAGINATED,
    data: { detailCode: 'PBLPF' }
  },
  {
    type: RECALL_NOTIFICATION,
    data: { detailCode: 'PBLPR' }
  },
  {
    type: DELETE_NOTIFICATION,
    data: { detailCode: 'PBLPD' }
  }
]

const DEFAULT_SORT = {
  hiddenBefore: { direction: 'DESC', position: 2 }
}

const NUMBER_PER_PAGE = 10

const SORT_ORDER_MAP = {
  ASC: 'ascending',
  DESC: 'descending'
}

function SpacePublisherPage(props: any) {
  const history = useHistory()
  const location = useLocation()

  const [state, _setState] = useState({
    showDeleted: false,
    sortingColumn: DEFAULT_SORT
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    async function componentDidMount() {
      await props.actions.resetRequests([DELETE_NOTIFICATION, FETCH_NOTIFICATIONS_PAGINATED, RECALL_NOTIFICATION])
      loadInitialPage()
    }
    componentDidMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    async function componentDidUpdate(prevProps: any) {
      if (prevProps.currentSpaceId !== props.currentSpaceId) {
        await props.actions.resetRequests([DELETE_NOTIFICATION, RECALL_NOTIFICATION])
        loadInitialPage()
      } else if (prevProps.isFetching && !props.isFetching) {
        // prefetch data needed by the notification builder to decrease the user's wait time
        props.actions.fetchAttributes()
        props.actions.fetchNotificationSources()
      } else if (prevProps.isDeleting && !props.isDeleting) {
        loadInitialPage()
      } else if (prevProps.isRecalling && !props.isRecalling) {
        loadInitialPage()
      }
    }
    if (prevProps) componentDidUpdate(prevProps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  const loadInitialPage = () => {
    loadPage(1, state.showDeleted, Object.keys(DEFAULT_SORT)[0], Object.values(DEFAULT_SORT)[0].direction)
  }

  const loadPage = (page: any, showDeleted: any, sortBy: any, sortOrder: any) => {
    setState({
      showDeleted,
      sortingColumn: sortBy && sortOrder ? { [sortBy]: { direction: sortOrder.toLowerCase() } } : {}
    })

    props.actions.fetchMultilingualNotificationsPaginated({
      count: NUMBER_PER_PAGE,
      includeDeleted: showDeleted,
      sortBy,
      // @ts-expect-error
      sortOrder: SORT_ORDER_MAP[(sortOrder || '').toUpperCase()],
      startIndex: (page - 1) * NUMBER_PER_PAGE + 1
    })
  }

  const navigateToBuilder = () => {
    history.push(`${location.pathname}/builder`)
  }

  const navigateToClonedBuilder = (notificationId: any) => {
    history.push(`${location.pathname}/${notificationId}/clone`)
  }

  const navigateToEditNotification = (notificationId: any) => {
    history.push(`${location.pathname}/builder/${notificationId}`)
  }

  const navigateToLogs = (correlationId: any) => {
    history.push(`${location.pathname}/${correlationId}/logs`)
  }

  const onDelete = (notificationId: any) => {
    props.actions.deleteMultilingualNotification(notificationId)
  }

  const onRecall = (notificationId: any) => {
    props.actions.recallMultilingualNotification(notificationId)
  }

  const { notifications, startIndex, totalResults, isRecalling, isDeleting, isFetching } = props
  const { showDeleted, sortingColumn } = state

  return (
    <Page isLoading={isFetching} errorsToHandle={errorsToHandle}>
      {notifications.length > 0 && <Page.Header title={<FormattedMessage {...messages.title} />} />}
      {notifications.length === 0 ? (
        <LandingStep onStart={navigateToBuilder} />
      ) : (
        <PublisherNotificationList
          isDeleting={isDeleting}
          isRecalling={isRecalling}
          loadPage={loadPage}
          notifications={notifications}
          notificationsStart={startIndex}
          notificationsTotal={totalResults}
          onCloneNotification={navigateToClonedBuilder}
          onCreateNotification={navigateToBuilder}
          onDeleteNotification={onDelete}
          onRecallNotification={onRecall}
          onNavigateToViewLogs={navigateToLogs}
          onSelectNotification={navigateToEditNotification}
          perPage={NUMBER_PER_PAGE}
          showDeleted={showDeleted}
          sortingColumn={sortingColumn}
        />
      )}
    </Page>
  )
}

SpacePublisherPage.displayName = 'SpacePublisherPage'

SpacePublisherPage.propTypes = {
  actions: PropTypes.shape({
    deleteMultilingualNotification: PropTypes.func.isRequired,
    fetchAttributes: PropTypes.func.isRequired,
    fetchMultilingualNotificationsPaginated: PropTypes.func.isRequired,
    fetchNotificationSources: PropTypes.func.isRequired,
    recallMultilingualNotification: PropTypes.func.isRequired,
    resetRequests: PropTypes.func.isRequired,
    startNotification: PropTypes.func.isRequired
  }).isRequired,
  currentSpaceId: PropTypes.string.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isRecalling: PropTypes.bool.isRequired,
  notifications: PropTypes.array.isRequired,
  startIndex: PropTypes.number.isRequired,
  totalResults: PropTypes.number.isRequired
}

export { /** @deprecated */ SpacePublisherPage }

const mapStateToProps = (state: any) => ({
  isDeleting: isRequestPending(state, DELETE_NOTIFICATION),
  isError: isRequestError(state, [DELETE_NOTIFICATION, FETCH_NOTIFICATIONS_PAGINATED, RECALL_NOTIFICATION]),
  isFetching: isRequestPending(state, FETCH_NOTIFICATIONS_PAGINATED),
  isRecalling: isRequestPending(state, RECALL_NOTIFICATION),
  notifications: getNotificationsPage(state),
  startIndex: getPageStartIndex(state),
  totalResults: getTotalNotificationCount(state),
  currentSpaceId: getCurrentSpaceId(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      deleteMultilingualNotification,
      fetchAttributes,
      fetchMultilingualNotificationsPaginated,
      fetchNotificationSources,
      recallMultilingualNotification,
      resetRequests,
      startNotification
    },
    dispatch
  )
})

export default connect(mapStateToProps, mapDispatchToProps)(SpacePublisherPage)
